import React, { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Box, Button, ButtonBase, Divider, IconButton, InputBase, Stack, Tooltip, Typography } from '@mui/material';

import { lensFilterMembers } from '~/hooks/apis/reporting/lensFilterMembers';
import useCurrentUser from '~/hooks/useCurrentUser';

import AddFilter from './AddFilter';
import CustomPeriod from './CustomPeriod';
import FilterSidePanel from './FilterSidePanel';
import FilterLayoutEditor from './FilterLayoutEditor';
import GroupedFilterPanel, { type GroupedFilterControl } from './GroupedFilterPanel';
import MultiPick from './MultiPick';
import ParamControls from './ParamControls';
import RangePick from './RangePick';
import { isSingleFilter } from './filterGrouping';
import { railParamNames, resolvedFilterGroups } from './filterLayout';
import filterSelectionSummary from './filterSelectionSummary';
import { memberLabel, presetLabel } from './format';
import { switchesInTitles, withEntry } from './lensState';
import type { LensDocument, LensDocumentData, LensState, SqlParam } from './types';
import type { BoardEditor } from './useBoardEdit';

export type ControlsPlacement = 'top' | 'side';

type LensControlsProps = {
  document: LensDocument;
  state: LensState;
  change?: (next: LensState) => void;
  filterLabel: (column: string) => string;
  options: (column: string) => Array<string>;
  optionMeta?: LensDocumentData['optionMeta'];
  tabs?: Array<string>;
  reach?: Set<string>;
  folded?: Array<string>;
  placement: ControlsPlacement;
  narrow: boolean;
  open: boolean;
  onOpen: (open: boolean) => void;
  withPeriod?: boolean;
  only?: 'period';
  lens?: { id: string; version: number };
  editor?: BoardEditor;
};

const SIDE_FROM = 7;
const SEARCH_FROM = 10;

function controlCount(document: LensDocument): number {
  const params = railParamNames(document).length;
  return (document.period.presets.length > 1 ? 1 : 0) + params + document.filters.length;
}

function panelParams(document: LensDocument): Set<string> {
  return new Set(railParamNames(document));
}

export function filtersSet(document: LensDocument, state: LensState): number {
  const params = panelParams(document);
  return (
    Object.keys(state.params ?? {}).filter(
      (name) => params.has(name) && (document.params[name] as SqlParam).role !== 'measure',
    ).length + Object.values(state.filters ?? {}).filter((values) => values?.length).length
  );
}

export function hasFilterControls(document: LensDocument): boolean {
  return panelParams(document).size > 0 || document.filters.length > 0;
}

export function placementOf(document: LensDocument): ControlsPlacement {
  const asked = document.display.filters ?? 'auto';
  if (asked !== 'auto') {
    return asked;
  }
  return controlCount(document) >= SIDE_FROM ? 'side' : 'top';
}

export default function LensControls({
  document,
  state,
  change,
  filterLabel,
  options,
  optionMeta,
  tabs,
  folded,
  placement,
  narrow,
  open,
  onOpen,
  withPeriod = true,
  only,
  lens,
  editor,
  reach,
}: LensControlsProps) {
  const { user } = useCurrentUser();
  const [match, setMatch] = useState('');
  const [organising, setOrganising] = useState(false);
  const applies = (key: string) => Boolean(editor) || !reach || reach.has(key);
  const paramApplies = (name: string) => Boolean((document.params[name] as SqlParam).role) || applies(`$${name}`);
  const matches = (column: string) => filterLabel(column).toLowerCase().includes(match.trim().toLowerCase());
  const memberSearch = (column: string) =>
    lens && optionMeta?.[column]?.status !== 'complete'
      ? lensFilterMembers(lens, state, column, user?.email, tabs, folded)
      : undefined;

  const period =
    withPeriod && (document.period.presets.length > 1 || change) ? (
      <Box aria-label="Period" className="seg" role="group">
        {document.period.presets.map((preset) => (
          <ButtonBase
            className={(state.period ?? document.period.default) === preset ? 'on' : ''}
            disabled={!change}
            key={preset}
            onClick={() => change?.({ ...state, period: preset === document.period.default ? undefined : preset })}
            type="button"
          >
            {presetLabel(preset)}
          </ButtonBase>
        ))}
        <CustomPeriod
          disabled={!change}
          editor={editor}
          document={document}
          onPick={(next) => change?.({ ...state, period: next })}
          period={state.period}
          presets={document.period.presets}
        />
      </Box>
    ) : null;
  if (only === 'period') {
    return period;
  }
  const switches = document.filters
    .filter((filter) => filter.style === 'segmented' && matches(filter.column))
    .map((filter) => {
      const chosen = state.filters?.[filter.column]?.[0];
      return (
        <Box aria-label={filterLabel(filter.column)} className="seg" key={filter.column} role="group">
          <ButtonBase
            className={chosen ? '' : 'on'}
            disabled={!change}
            onClick={() => change?.({ ...state, filters: withEntry(state.filters, filter.column, undefined) })}
            type="button"
          >
            All
          </ButtonBase>
          {[...new Set([...options(filter.column), ...(state.filters?.[filter.column] ?? [])])].sort().map((option) => (
            <ButtonBase
              className={chosen === option ? 'on' : ''}
              disabled={!change}
              key={option}
              onClick={() => change?.({ ...state, filters: withEntry(state.filters, filter.column, [option]) })}
              type="button"
            >
              {memberLabel(option)}
              {optionMeta?.[filter.column]?.selected.some(
                (entry) => entry.value === option && entry.available === false,
              )
                ? ' · no data with current filters'
                : ''}
            </ButtonBase>
          ))}
          {optionMeta?.[filter.column]?.status === 'not_linked' ? (
            <Typography variant="caption" role="status">
              Not linked
            </Typography>
          ) : null}
        </Box>
      );
    });
  const isSet = (column: string) => Boolean(state.filters?.[column]?.length || state.ranges?.[column]);
  const ranged = (column: string, onRemove?: () => void) => (
    <RangePick
      key={column}
      label={filterLabel(column)}
      onPick={
        change
          ? (next) => change({ ...state, ranges: withEntry(state.ranges, column, next ?? (onRemove ? {} : undefined)) })
          : undefined
      }
      onRemove={onRemove}
      range={state.ranges?.[column] ?? document.filters.find((filter) => filter.column === column)?.range}
    />
  );
  const picker = (filter: LensDocument['filters'][number]) =>
    filter.style === 'range' ? (
      ranged(filter.column)
    ) : (
      <MultiPick
        key={filter.column}
        label={filterLabel(filter.column)}
        onPick={
          change ? (next) => change({ ...state, filters: withEntry(state.filters, filter.column, next) }) : undefined
        }
        options={[...new Set([...options(filter.column), ...(state.filters?.[filter.column] ?? [])])].sort()}
        picked={state.filters?.[filter.column] ?? []}
        single={isSingleFilter(filter.multiple)}
        search={memberSearch(filter.column)}
        meta={optionMeta?.[filter.column]}
        filterLabel={filterLabel}
      />
    );
  const pickable = document.filters.filter((filter) => filter.style !== 'segmented' && matches(filter.column));
  const declared = new Set(document.filters.map((filter) => filter.column));
  const remove = (key: 'filters' | 'ranges', column: string) =>
    change?.({ ...state, [key]: withEntry(state[key] as Record<string, unknown> | undefined, column, undefined) });
  const extras = [
    ...Object.keys(state.filters ?? {})
      .filter((column) => !declared.has(column) && matches(column) && applies(column))
      .map((column) => (
        <MultiPick
          key={column}
          label={filterLabel(column)}
          onPick={change ? (next) => change({ ...state, filters: withEntry(state.filters, column, next) }) : undefined}
          onRemove={change ? () => remove('filters', column) : undefined}
          options={[...options(column)].sort()}
          picked={state.filters?.[column] ?? []}
          search={memberSearch(column)}
          meta={optionMeta?.[column]}
          filterLabel={filterLabel}
        />
      )),
    ...Object.keys(state.ranges ?? {})
      .filter((column) => !declared.has(column) && matches(column) && applies(column))
      .map((column) => ranged(column, change ? () => remove('ranges', column) : undefined)),
  ];
  const adder =
    change && lens ? (
      <AddFilter
        key="add-filter"
        lensId={lens.id}
        lensVersion={lens.version}
        onAdd={(column, numeric) =>
          change(
            numeric
              ? { ...state, ranges: { ...state.ranges, [column]: {} } }
              : { ...state, filters: { ...state.filters, [column]: [] } },
          )
        }
        taken={new Set([...declared, ...Object.keys(state.filters ?? {}), ...Object.keys(state.ranges ?? {})])}
      />
    ) : null;
  const pickers = [...pickable.filter((filter) => applies(filter.column)).map(picker), ...extras, adder];

  const paramNames = railParamNames(document);
  const groupedControls: Array<GroupedFilterControl> = [
    ...paramNames.map((name) => {
        const param = document.params[name] as SqlParam;
        const label = param.label ?? name;
        return {
          id: `param:${name}`,
          label,
          active: state.params?.[name] !== undefined,
          summary: param.multiple
            ? filterSelectionSummary(state.params?.[name]?.split('|') ?? param.value ?? [], param.options, true)
            : state.params?.[name] ?? param.options[0],
          node: <ParamControls all change={change} document={document} names={[name]} state={state} />,
        };
      }),
    ...document.filters.map((filter) => ({
      id: `filter:${filter.column}`,
      label: filterLabel(filter.column),
      active: isSet(filter.column),
      summary: filterSelectionSummary(
        state.filters?.[filter.column] ?? [],
        options(filter.column),
        optionMeta?.[filter.column]?.status === 'complete',
      ),
      node: filter.style === 'segmented' ? switches.find((node) => node.key === filter.column) : picker(filter),
    })),
  ];
  const resetGroup = (ids: Array<string>) => {
    let params = state.params;
    let filters = state.filters;
    let ranges = state.ranges;
    ids.forEach((id) => {
      const [kind, key] = id.split(':');
      if (kind === 'param') {
        params = withEntry(params, key, undefined);
      } else {
        filters = withEntry(filters, key, undefined);
        ranges = withEntry(ranges, key, undefined);
      }
    });
    change?.({ ...state, params, filters, ranges });
  };

  const inTitles = switchesInTitles(document);
  const topParams = paramNames.filter((name) => !inTitles.has(name));
  if (placement === 'top') {
    return (
      <>
        {period}
        <ParamControls change={change} document={document} names={topParams.filter(paramApplies)} state={state} />
        {switches.filter((node) => applies(String(node.key)))}
        {pickers}
      </>
    );
  }

  if (!open) {
    return period;
  }
  return (
    <>
      {period}
      <FilterSidePanel narrow={narrow} onOpen={onOpen}>
        {controlCount(document) >= SEARCH_FROM ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              minHeight: 36,
              px: 0.75,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.25,
              bgcolor: 'background.default',
              transition: 'border-color 120ms',
              '&:focus-within': { borderColor: 'primary.main' },
            }}
          >
            <SearchIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />
            <InputBase
              inputProps={{ 'aria-label': 'Find a filter' }}
              onChange={(event) => setMatch(event.target.value)}
              placeholder="Find a filter"
              sx={{ flex: 1, minWidth: 0, fontSize: 12, '& input': { py: 0.75, px: 0.25 } }}
              value={match}
            />
            {match ? (
              <IconButton aria-label="Clear filter search" onClick={() => setMatch('')} size="small" sx={{ width: 24, height: 24, color: 'text.secondary' }}>
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            ) : null}
            {editor ? (
              <>
                <Divider orientation="vertical" sx={{ height: 18 }} />
                <Tooltip title="Organise filters">
                  <IconButton
                    aria-label="Organise filters"
                    onClick={() => setOrganising(true)}
                    size="small"
                    sx={{ width: 28, height: 28, borderRadius: 1, color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'action.hover' } }}
                  >
                    <TuneOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </>
            ) : null}
          </Stack>
        ) : editor ? (
          <Button onClick={() => setOrganising(true)} size="small" startIcon={<TuneOutlinedIcon sx={{ fontSize: 16 }} />} sx={{ fontSize: 12, alignSelf: 'flex-start' }}>Organise filters</Button>
        ) : null}
        <Stack spacing={1}>
          <GroupedFilterPanel
            controls={groupedControls.filter((control) => {
              const [kind, key] = control.id.split(':');
              return kind === 'param' ? paramApplies(key) : applies(key);
            })}
            groups={resolvedFilterGroups(document)}
            match={match}
            onReset={change ? resetGroup : undefined}
          />
          {extras}
          {adder}
          {change && filtersSet(document, state) ? (
            <Button
              onClick={() => change({ ...state, params: undefined, filters: undefined, ranges: undefined })}
              size="small"
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              Reset all filters
            </Button>
          ) : null}
        </Stack>
        {narrow ? (
          <ButtonBase className="side-done" onClick={() => onOpen(false)} type="button">
            Done
          </ButtonBase>
        ) : null}
      </FilterSidePanel>
      {organising && editor ? <FilterLayoutEditor document={document} editor={editor} onClose={() => setOrganising(false)} /> : null}
    </>
  );
}
